import { ModelVisibilityConfig } from '@components/3DControls/MaterialMutation/FadeModel';
import { PartNode } from '@partful/shared-types';
import type { KitComponent } from '@partful/shared-types/';
import { useKit } from '@services/kits/kit';
import { getKitComponentMeshIds } from '@services/kits/utils';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scene } from 'src/components/Scene';
import { useCurrentTabName } from 'src/hooks/useCurrentTabName';
import { ModelContext } from 'src/providers/Model';
import { useProduct } from 'src/providers/Product';
import { RepairDetailContext } from 'src/providers/repairs/RepairDetails';
import { usePart } from 'src/services/part';
import { Box3, Object3D, Vector3 } from 'three';
import { CanvasFlexWrapper } from './CanvasFlexWrapper';
import { ErrorMessage } from './ErrorMessage';
import LoadingOverlay from './LoadingOverlay';

interface GetAssemblyHighlightsParams {
  currentPartNode: PartNode;
  hiddenVariantNames?: string[];
}
/* istanbul ignore next */
/**
 * Parses the current part node and returns a list of object3DNames to highlight
 * Needs to go through children to include variants
 * @returns list of object3DNames to highlight
 */
function getAssemblyHighlights({ currentPartNode, hiddenVariantNames }: GetAssemblyHighlightsParams) {
  const get3dNames = (child: PartNode): string | string[] => {
    if (child.object3DName) return hiddenVariantNames?.includes(child.object3DName) ? [] : child.object3DName;

    // variant case
    if (child.children) return child.children.flatMap(get3dNames);

    return [];
  };

  const assemblyHighlight = currentPartNode.children.flatMap(get3dNames);

  return assemblyHighlight;
}

const isString = (x: string | undefined): x is string => typeof x === 'string';

/**
 * Determines which parts should be shown and hidden in the 3D model based on various contexts
 * @param {Object} params - The parameters object
 * @param {Object} [params.focalPoint] - The focal point configuration
 * @param {string[]} [params.focalPoint.show] - Parts to show when clicking a hotspot
 * @param {string[]} [params.focalPoint.hide] - Parts to hide when clicking a hotspot
 * @param {Object} [params.currentRepairStep] - The current work instruction step configuration
 * @param {string[]} [params.currentRepairStep.showNames] - Parts to show during work instruction step
 * @param {string[]} [params.currentRepairStep.hideNames] - Parts to hide during work instruction step
 * @param {Object} [params.currentKit] - The selected kit
 * @param {KitComponent[]} params.currentKit.components - Components in the selected kit
 * @param {PartNode} [params.bom] - The bill of materials (part tree)
 * @param {string[]} [params.hiddenVariantNames] - Names of variants that should be hidden
 * @returns {Object} Object containing arrays of meshIDs of parts to show/hide
 *
 * @warning show/hide are purposefully returned as undefined - this has different behaviour to empty array
 */
export function determineModelVisibility({
  focalPoint,
  currentRepairStep,
  currentKit,
  bom,
  hiddenVariantNames,
}: {
  focalPoint?: { show?: string[]; hide?: string[] };
  currentRepairStep?: { showNames?: string[]; hideNames?: string[] };
  currentKit?: { components: KitComponent[] };
  bom?: PartNode;
  hiddenVariantNames?: string[];
}): ModelVisibilityConfig {
  if (currentKit) {
    const kitComponentsShownInCad = getKitComponentMeshIds(currentKit.components, bom);

    return {
      // Hides all componenets when there are no matching ones by having fademodel show only a meshID that can not exist
      show: kitComponentsShownInCad.length === 0 ? ['.HIDE_ALL_COMPONENTS'] : kitComponentsShownInCad,
      hide: [],
      hiddenVariantNames: [],
    };
  }

  return {
    show: focalPoint?.show ?? currentRepairStep?.showNames,
    hide: focalPoint?.hide ?? currentRepairStep?.hideNames,
    hiddenVariantNames: currentRepairStep ? [] : hiddenVariantNames,
  };
}

export function ThreeJsContainer({ fullControls }: { fullControls: boolean }) {
  const { t } = useTranslation('threeDCanvas');
  const { model, animations, loadingProgress, isError, isNetworkError, postProcessingMode } = useContext(ModelContext);
  const [fullyRendered, setFullyRendered] = useState(false);

  const { step: currentRepairStep } = useContext(RepairDetailContext) || {};
  const part = usePart();
  const { current, focalPoint } = part;
  const { hiddenVariantNames, bom } = useProduct();
  const isPartCatalogue = useCurrentTabName() === 'parts-catalogue';
  const { currentKit } = useKit();

  const fadeModel = useMemo(
    () => determineModelVisibility({ focalPoint, currentRepairStep, currentKit, bom, hiddenVariantNames }),
    [focalPoint, currentRepairStep, currentKit, bom, hiddenVariantNames]
  );

  const isCurrentAnAssembly = !!current?.price && /* istanbul ignore next */ !!current.children.length;
  const assemblyHighlight = isCurrentAnAssembly
    ? /* istanbul ignore next */ getAssemblyHighlights({ currentPartNode: current, hiddenVariantNames })
    : [];
  const currentPartHighlight = [current?.object3DName].concat(assemblyHighlight).filter(isString);

  const highlight = useMemo(
    () =>
      currentRepairStep?.highlight?.names || // Old repair highlights, deprecated with https://bitbucket.org/samson_vt/svt-blender-plugin/pull-requests/52
      currentRepairStep?.highlightNames || // New naming
      currentPartHighlight, // Part highlight
    // TODO: Highlight gets' updated with every usePart() destructuration. Fix it on top level and remove eslint disable.
    // Updated deps array to include all possible values that can be used in highlight to fix https://samsonvt.atlassian.net/browse/SVT-2008
    [currentPartHighlight, currentRepairStep?.highlight?.names, currentRepairStep?.highlightNames]
  );

  const clip = useMemo(() => {
    const clipName = currentRepairStep?.animationClipName || focalPoint?.animation;
    return clipName
      ? /* istanbul ignore next */ animations?.find(/* istanbul ignore next */ (clip) => clip.name === clipName)
      : undefined;
  }, [currentRepairStep, focalPoint, animations]);

  const { orbitCenter, position } = useMemo(
    () => ({
      orbitCenter: focalPoint?.target ?? currentRepairStep?.orbitCenter,
      position: focalPoint?.position ?? currentRepairStep?.cameraPosition,
    }),
    [focalPoint, currentRepairStep]
  );
  /* istanbul ignore next */

  if (isError) {
    if (isNetworkError) {
      return (
        <ErrorMessage data-testid="three-container-network-error">
          <b>{t('errors.networkErrorTitle')}</b> <br />
          {t('errors.networkErrorMessage')}
        </ErrorMessage>
      );
    }
    return <ErrorMessage>{t('errors.renderUnavailable')}</ErrorMessage>;
  }

  const bbox = getBoundingBox(model);
  /* istanbul ignore next */
  return (
    <CanvasFlexWrapper data-testid="three-container">
      {model && (
        <Scene
          setFullyRendered={setFullyRendered}
          scene={model}
          clip={clip}
          fadeModel={fadeModel}
          highlight={highlight}
          orbitCenter={orbitCenter}
          position={position}
          isPartCatalogue={isPartCatalogue}
          fullControls={fullControls}
          modelBoundingBox={bbox}
          postProcessingMode={postProcessingMode}
        />
      )}
      {(!fullyRendered || !model) && <LoadingOverlay progress={loadingProgress} />}
    </CanvasFlexWrapper>
  );
}

export function getBoundingBox(model: Object3D | undefined) {
  return model ? new Box3().setFromObject(model) : new Box3().set(new Vector3(2, 2, 2), new Vector3(2, 2, 2));
}
